"use client"

import type { Project } from "@/lib/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { FolderIcon, Calendar, DollarSign, Plus } from 'lucide-react';
import { getStatusColors } from '@/lib/status-colors';
import { format } from "date-fns"

interface ProjectsKanbanBoardProps {
  projects: Project[]
  onSelectProject?: (project: Project) => void
  selectedProject?: Project | null
}

type ProjectKanbanColumn = {
  id: string
  title: string
  status: string // Now represents group identifier
  color: string
  count: number
}

export function ProjectsKanbanBoard({ projects, onSelectProject, selectedProject }: ProjectsKanbanBoardProps) {
  // Define project status groups - each group contains multiple statuses in specific order
  const statusGroups = [
    {
      id: "to-do",
      title: "To-do",
      color: "border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/50",
      statuses: ["Planning", "Backlog"]
    },
    {
      id: "in-progress", 
      title: "In Progress",
      color: "border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/50",
      statuses: ["In Progress", "Maintenance", "Paused"]
    },
    {
      id: "complete",
      title: "Complete", 
      color: "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50",
      statuses: ["Done", "Cancelled"]
    }
  ]

  // Create columns for the 3 main groups
  const columns: ProjectKanbanColumn[] = statusGroups.map((group) => ({
    id: group.id,
    title: group.title,
    status: group.id, // Use group id as status identifier
    color: group.color,
    count: projects.filter(p => p.status && group.statuses.includes(p.status)).length
  }))

  const getProjectsForColumn = (groupId: string) => {
    const group = statusGroups.find(g => g.id === groupId)
    if (!group) return []
    
    // Get projects for this group and sort them by the status order defined in the group
    const groupProjects = projects.filter(project => project.status && group.statuses.includes(project.status))
    
    // Sort projects by status order within the group
    return groupProjects.sort((a, b) => {
      const aIndex = group.statuses.indexOf(a.status || '')
      const bIndex = group.statuses.indexOf(b.status || '')
      return aIndex - bIndex
    })
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  }

  return (
    <div className="space-y-6">
      {/* Kanban Board Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground dark:text-gray-100">Project Board</h2>
          <p className="text-muted-foreground dark:text-gray-400">
            Organize and track your projects across different stages
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          {projects.length} total projects
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-h-[600px]">
        {columns.map((column) => (
          <div key={column.id} className={`rounded-lg border-2 ${column.color} p-4`}>
            {/* Column Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">{column.title}</h3>
                <Badge variant="secondary" className="text-xs">
                  {column.count}
                </Badge>
              </div>
              <div className="w-full h-1 bg-current opacity-20 rounded-full"></div>
            </div>

            {/* Column Content */}
            <ScrollArea className="h-[500px] pr-2">
              <div className="space-y-3">
                {getProjectsForColumn(column.id).map((project) => (
                  <Card 
                    key={project.id}
                    className={`border-l-4 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${
                      selectedProject?.id === project.id 
                        ? 'ring-2 ring-blue-500 shadow-md' 
                        : ''
                    }`}
                    style={{ borderLeftColor: getStatusColors(project.status || '').borderColorValue }}
                    onClick={() => onSelectProject?.(project)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <CardTitle className="text-sm font-medium leading-tight line-clamp-2">
                            {project.name}
                          </CardTitle>
                        </div>
                        {project.status && (
                          <Badge 
                            className={`${getStatusColors(project.status).bg} ${getStatusColors(project.status).text} ${getStatusColors(project.status).border} border shrink-0 text-xs`}
                          >
                            {project.status}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {/* Project Description */}
                        {project.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {project.description}
                          </p>
                        )}
                        
                        {/* Project Meta Information */}
                        <div className="space-y-1">
                          {project.task_count !== undefined && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span>{project.task_count} task{project.task_count !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                          
                          {project.budget !== undefined && (
                            <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                              <DollarSign className="h-3 w-3" />
                              <span>${project.budget.toLocaleString()}</span>
                            </div>
                          )}

                          {project.last_edited_time && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>Updated {formatDate(project.last_edited_time)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Empty State for Column */}
                {getProjectsForColumn(column.id).length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                      <Plus className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">No projects in {column.title.toLowerCase()}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
    </div>
  )
}